import { useCallback, useEffect, useRef } from 'react';
import { useMap, useMapEvents } from 'react-leaflet';
import { useNetworkMapStore } from '../../store/networkMapStore';
import { useNetworkUIStore } from '../../store/networkUIStore';
import { LeafletMouseEvent } from 'leaflet';
import debounce from 'lodash/debounce';
import throttle from 'lodash/throttle';

export function MapPositionHandler() {
  const map = useMap();
  const setMapPosition = useNetworkMapStore(state => state.setMapPosition);
  const currentPosition = useNetworkMapStore(state => state.mapPosition);
  const isMovingRef = useRef(false);
  const currentPositionRef = useRef(currentPosition);
  const setMapPositionRef = useRef(setMapPosition);

  // Keep refs in sync with state/props
  useEffect(() => {
    currentPositionRef.current = currentPosition;
  }, [currentPosition]);

  useEffect(() => {
    setMapPositionRef.current = setMapPosition;
  }, [setMapPosition]);

  // Store throttled/debounced functions in refs to avoid recreation
  const throttledPositionUpdateRef = useRef<ReturnType<typeof throttle> | null>(null);
  const debouncedSetPositionRef = useRef<ReturnType<typeof debounce> | null>(null);

  // Initialize throttled/debounced functions in effect
  useEffect(() => {
    throttledPositionUpdateRef.current = throttle((center: [number, number], zoom: number) => {
      const pos = currentPositionRef.current;
      if (
        Math.abs(center[0] - pos.center[0]) > 0.01 ||
        Math.abs(center[1] - pos.center[1]) > 0.01 ||
        zoom !== pos.zoom
      ) {
        setMapPositionRef.current({
          center,
          zoom
        });
      }
    }, 250);

    debouncedSetPositionRef.current = debounce((center: [number, number], zoom: number) => {
      const pos = currentPositionRef.current;
      if (
        center[0] !== pos.center[0] ||
        center[1] !== pos.center[1] ||
        zoom !== pos.zoom
      ) {
        setMapPositionRef.current({
          center,
          zoom
        });
      }
      isMovingRef.current = false;
    }, 150);

    return () => {
      throttledPositionUpdateRef.current?.cancel();
      debouncedSetPositionRef.current?.cancel();
    };
  }, []);

  // Handle map movements with optimized event handling
  useMapEvents({
    movestart: () => {
      isMovingRef.current = true;
    },
    move: () => {
      if (isMovingRef.current) {
        const center = map.getCenter();
        const zoom = map.getZoom();
        throttledPositionUpdateRef.current?.([center.lat, center.lng], zoom || 19);
      }
    },
    moveend: () => {
      const center = map.getCenter();
      const zoom = map.getZoom();
      debouncedSetPositionRef.current?.([center.lat, center.lng], zoom || 19);
    }
  });

  return null;
}

export function MapClickHandler() {
  const setSelectedElement = useNetworkUIStore(state => state.setSelectedElement);

  // Optimize click handler with useCallback to prevent recreation
  const handleMapClick = useCallback((e: LeafletMouseEvent) => {
    // Get the click path
    const path = e.originalEvent.composedPath?.() || [];

    // Use a single loop instead of multiple some() calls for better performance
    let isMarkerOrLineClick = false;

    for (let i = 0; i < path.length; i++) {
      const element = path[i] as HTMLElement;
      if (element.classList) {
        if (
          element.classList.contains('leaflet-marker-icon') ||
          element.classList.contains('leaflet-marker-shadow') ||
          element.classList.contains('leaflet-interactive')
        ) {
          isMarkerOrLineClick = true;
          break;
        }
      }
    }

    // Only clear selection if we didn't click on a marker or line
    // Sidebar collapse is handled automatically via useEffect in Sidebar.tsx
    if (!isMarkerOrLineClick) {
      setSelectedElement(null);
    }
  }, [setSelectedElement]);

  useMapEvents({
    click: handleMapClick
  });

  return null;
}
